import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { notification, Grid } from "antd";
import moment from "moment";

import { Input, Button, Box } from "components";
import { SendCompleteIcon, DownloadIcon } from "icons";
import { generateCSV, localStorageHelper } from "utils";
import { GetDashboardInfoActionCreators } from "redux/rootActions";

import {
  selectIsLoadingDashboardInfo,
  selectDashboardInfoErrorMessage,
  dashboardInfoResponse,
} from "redux/dashboard/dashboardInfo/dashboardInfoStates";

import {
  WrapperContent,
  SectionLabel,
  ItemsWrapper,
  ItemSingle,
  ItemDouble,
} from "./Dashboard.style";

const { useBreakpoint } = Grid;
// md 768

const csvHeaders = [
  { label: "日付", key: "date" },
  { label: "区分", key: "classification" },
  { label: "カウント種別", key: "attribute" },
  { label: "合計", key: "total" },
  { label: "男", key: "male" },
  { label: "女", key: "female" },
  { label: "回答なし", key: "notAnswer" },
];

const Dashboard = () => {
  const screens = useBreakpoint();
  const dispatch = useDispatch();

  const contentRef = useRef<HTMLDivElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const isLoadingDashboard = useSelector(selectIsLoadingDashboardInfo);
  const dashboardError = useSelector(selectDashboardInfoErrorMessage);
  const dashboardData = useSelector(dashboardInfoResponse);

  const [dashboardInfo, setDashboardInfo] = useState<any>(undefined);
  const [showMargin, setShowMargin] = useState<boolean>(false);
  const [size, setSize] = useState<number>(-1);
  const [csvData, setCsvData] = useState<any[]>([]);

  const superCityName = localStorageHelper.getItem("supercity_name");

  const returnCsvData = (data: any) => {
    const tempData: any[] = [];
    const date = moment().format("YYYY/MM/DD");

    if (data?.patients) {
      tempData.push({
        date: date,
        classification: "登録者数",
        attribute: "総数",
        total: data.patients?.total || 0,
        male: data.patients?.gender_0 || 0,
        female: data.patients?.gender_1 || 0,
        notAnswer: data.patients?.gender_2 || 0,
      });
      if (data.patients?.consultation) {
        tempData.push({
          date: date,
          classification: "医療相談",
          attribute: "対象者",
          total: data.patients.consultation?.condition?.total || 0,
          male: data.patients.consultation?.condition?.gender_0 || 0,
          female: data.patients.consultation?.condition?.gender_1 || 0,
          notAnswer: data.patients.consultation?.condition?.gender_2 || 0,
        });
        if (data.patients.consultation?.is_household_based) {
          tempData.push({
            date: date,
            classification: "医療相談",
            attribute: "世帯対象者",
            total: data.patients.consultation?.not_condition?.total || 0,
            male: data.patients.consultation?.not_condition?.gender_0 || 0,
            female: data.patients.consultation?.not_condition?.gender_1 || 0,
            notAnswer: data.patients.consultation?.not_condition?.gender_2 || 0,
          });
        }
      }
      if (data.patients?.online_medical) {
        tempData.push({
          date: date,
          classification: "オンライン診療",
          attribute: "対象者",
          total: data.patients.online_medical?.condition?.total || 0,
          male: data.patients.online_medical?.condition?.gender_0 || 0,
          female: data.patients.online_medical?.condition?.gender_1 || 0,
          notAnswer: data.patients.online_medical?.condition?.gender_2 || 0,
        });
        if (data.patients.online_medical?.is_household_based) {
          tempData.push({
            date: date,
            classification: "オンライン診療",
            attribute: "世帯対象者",
            total: data.patients.online_medical?.not_condition?.total || 0,
            male: data.patients.online_medical?.not_condition?.gender_0 || 0,
            female: data.patients.online_medical?.not_condition?.gender_1 || 0,
            notAnswer:
              data.patients.online_medical?.not_condition?.gender_2 || 0,
          });
        }
      }
    }
    if (data?.consultations) {
      tempData.push({
        date: date,
        classification: "相談数",
        attribute: "総件数",
        total: data.consultations?.total || 0,
        male: data.consultations?.gender_0 || 0,
        female: data.consultations?.gender_1 || 0,
        notAnswer: data.consultations?.gender_2 || 0,
      });
      if (data.consultations?.condition) {
        tempData.push({
          date: date,
          classification: "相談数",
          attribute: "対象者件数",
          total: data.consultations.condition?.total || 0,
          male: data.consultations.condition?.gender_0 || 0,
          female: data.consultations.condition?.gender_1 || 0,
          notAnswer: data.consultations.condition?.gender_2 || 0,
        });
      }
      if (
        data.consultations?.not_condition &&
        data?.patients?.consultation &&
        data?.patients?.consultation?.is_household_based
      ) {
        tempData.push({
          date: date,
          classification: "相談数",
          attribute: "世帯対象者件数",
          total: data.consultations.not_condition?.total || 0,
          male: data.consultations.not_condition?.gender_0 || 0,
          female: data.consultations.not_condition?.gender_1 || 0,
          notAnswer: data.consultations.not_condition?.gender_2 || 0,
        });
      }
    }

    return tempData;
  };

  const downloadCsv = () => {
    if (csvData?.length) {
      const fileName = `${moment().format(
        "YYYY年MM月DD日"
      )}-${superCityName.replace("管理画面", "")}.csv`;

      generateCSV(fileName, csvHeaders, csvData);
    }
  };

  const checkContentHeight = useCallback(() => {
    if (contentRef.current) {
      const shouldShowMargin = contentRef.current.scrollHeight > 150;
      setShowMargin(shouldShowMargin);
    }
  }, []);

  useEffect(() => {
    // Initialize ResizeObserver
    resizeObserverRef.current = new ResizeObserver(() => {
      checkContentHeight();
    });

    if (contentRef.current) {
      resizeObserverRef.current.observe(contentRef.current);
    }

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setSize(window.innerWidth);
      // Add a small delay to ensure content has reflowed
      setTimeout(checkContentHeight, 100);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    dispatch(GetDashboardInfoActionCreators.getDashboardInfoAction());
  }, []);

  useEffect(() => {
    if (!isLoadingDashboard && dashboardData) {
      if (dashboardData.result) {
        setDashboardInfo(dashboardData.result);
        const tempCsvData: any[] = returnCsvData(dashboardData.result);
        setCsvData(tempCsvData);
      } else {
        setDashboardInfo(undefined);
        setCsvData([]);
      }
    }
  }, [isLoadingDashboard, dashboardData]);

  useEffect(() => {
    if (dashboardError) {
      setDashboardInfo(undefined);
      setCsvData([]);
      dispatch(GetDashboardInfoActionCreators.handleResetAction());
    }
  }, [dashboardError]);

  return (
    <Box title="ダッシュボード">
      <WrapperContent>
        <Button
          className="csv-button"
          icon={
            <DownloadIcon
              width="14px"
              height="14px"
              fill="currentColor"
              style={{ position: "absolute", left: 5, top: 8 }}
            />
          }
          name="CSV出力"
          background="#2AC769"
          color="#FFFFFF"
          border="none"
          fontSize={12}
          lineHeight="17px"
          fontWeight={700}
          padding="8px 8px 6px 17px"
          onClick={downloadCsv}
          // disabled={false}
        />
        <SectionLabel>登録者数</SectionLabel>
        <ItemsWrapper ref={contentRef} style={{ marginBottom: 32 }}>
          <ItemSingle>
            <div className="title">{screens.md ? "総数" : "登録総数"}</div>
            <div className="total-count">
              {dashboardInfo?.patients?.total}
              <span className="human">人</span>
              <span className="human">
                （{dashboardInfo?.patients?.total_users}世帯）
              </span>
            </div>
            <div className="seperate-count">
              <div>男:{dashboardInfo?.patients?.gender_0}</div>
              <div>女:{dashboardInfo?.patients?.gender_1}</div>
              <div>回答なし:{dashboardInfo?.patients?.gender_2}</div>
            </div>
          </ItemSingle>
          {screens.md ? (
            <React.Fragment>
              {dashboardInfo?.patients?.consultation ? (
                <ItemDouble>
                  <div className="wrapper">
                    <div className="child-item">
                      <div className="title">対象者</div>
                      <div className="total-count">
                        {
                          dashboardInfo?.patients?.consultation?.condition
                            ?.total
                        }
                        <span className="human">人</span>
                      </div>
                      <div className="seperate-count">
                        <div>
                          男:
                          {
                            dashboardInfo?.patients?.consultation?.condition
                              ?.gender_0
                          }
                        </div>
                        <div>
                          女:
                          {
                            dashboardInfo?.patients?.consultation?.condition
                              ?.gender_1
                          }
                        </div>
                        <div>
                          回答なし:
                          {
                            dashboardInfo?.patients?.consultation?.condition
                              ?.gender_2
                          }
                        </div>
                      </div>
                    </div>
                    {dashboardInfo?.patients?.consultation
                      ?.is_household_based ? (
                      <>
                        <div className="divider"></div>
                        <div className="child-item">
                          <div className="title">世帯対象者</div>
                          <div className="total-count">
                            {
                              dashboardInfo?.patients?.consultation
                                ?.not_condition?.total
                            }
                            <span className="human">人</span>
                          </div>
                          <div className="seperate-count">
                            <div>
                              男:
                              {
                                dashboardInfo?.patients?.consultation
                                  ?.not_condition?.gender_0
                              }
                            </div>
                            <div>
                              女:
                              {
                                dashboardInfo?.patients?.consultation
                                  ?.not_condition?.gender_1
                              }
                            </div>
                            <div>
                              回答なし:
                              {
                                dashboardInfo?.patients?.consultation
                                  ?.not_condition?.gender_2
                              }
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <></>
                    )}
                  </div>
                  <div className="upper-label">医療相談</div>
                </ItemDouble>
              ) : (
                <></>
              )}
              {dashboardInfo?.patients?.online_medical ? (
                <ItemDouble hasMargin={showMargin}>
                  <div className="wrapper">
                    <div className="child-item">
                      <div className="title">対象者</div>
                      <div className="total-count">
                        {
                          dashboardInfo?.patients?.online_medical?.condition
                            ?.total
                        }
                        <span className="human">人</span>
                      </div>
                      <div className="seperate-count">
                        <div>
                          男:
                          {
                            dashboardInfo?.patients?.online_medical?.condition
                              ?.gender_0
                          }
                        </div>
                        <div>
                          女:
                          {
                            dashboardInfo?.patients?.online_medical?.condition
                              ?.gender_1
                          }
                        </div>
                        <div>
                          回答なし:
                          {
                            dashboardInfo?.patients?.online_medical?.condition
                              ?.gender_2
                          }
                        </div>
                      </div>
                    </div>
                    {dashboardInfo?.patients?.online_medical
                      ?.is_household_based ? (
                      <>
                        <div className="divider"></div>

                        <div className="child-item">
                          <div className="title">世帯対象者</div>
                          <div className="total-count">
                            {
                              dashboardInfo?.patients?.online_medical
                                ?.not_condition?.total
                            }
                            <span className="human">人</span>
                          </div>
                          <div className="seperate-count">
                            <div>
                              男:
                              {
                                dashboardInfo?.patients?.online_medical
                                  ?.not_condition?.gender_0
                              }
                            </div>
                            <div>
                              女:
                              {
                                dashboardInfo?.patients?.online_medical
                                  ?.not_condition?.gender_1
                              }
                            </div>
                            <div>
                              回答なし:
                              {
                                dashboardInfo?.patients?.online_medical
                                  ?.not_condition?.gender_2
                              }
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <></>
                    )}
                  </div>
                  <div className="upper-label">オンライン診療</div>
                </ItemDouble>
              ) : (
                <></>
              )}
            </React.Fragment>
          ) : (
            <React.Fragment>
              {dashboardInfo?.patients?.consultation ? (
                <>
                  <ItemSingle>
                    <div className="title">医療相談対象者</div>
                    <div className="total-count">
                      {dashboardInfo?.patients?.consultation?.condition?.total}
                      <span className="human">人</span>
                    </div>
                    <div className="seperate-count">
                      <div>
                        男:
                        {
                          dashboardInfo?.patients?.consultation?.condition
                            ?.gender_0
                        }
                      </div>
                      <div>
                        女:
                        {
                          dashboardInfo?.patients?.consultation?.condition
                            ?.gender_1
                        }
                      </div>
                      <div>
                        回答なし:
                        {
                          dashboardInfo?.patients?.consultation?.condition
                            ?.gender_2
                        }
                      </div>
                    </div>
                  </ItemSingle>
                  {dashboardInfo?.patients?.consultation?.is_household_based ? (
                    <>
                      <ItemSingle>
                        <div className="title">医療相談世帯対象者</div>
                        <div className="total-count">
                          {
                            dashboardInfo?.patients?.consultation?.not_condition
                              ?.total
                          }
                          <span className="human">人</span>
                        </div>
                        <div className="seperate-count">
                          <div>
                            男:
                            {
                              dashboardInfo?.patients?.consultation
                                ?.not_condition?.gender_0
                            }
                          </div>
                          <div>
                            女:
                            {
                              dashboardInfo?.patients?.consultation
                                ?.not_condition?.gender_1
                            }
                          </div>
                          <div>
                            回答なし:
                            {
                              dashboardInfo?.patients?.consultation
                                ?.not_condition?.gender_2
                            }
                          </div>
                        </div>
                      </ItemSingle>
                    </>
                  ) : (
                    <></>
                  )}
                </>
              ) : (
                <></>
              )}
              {dashboardInfo?.patients?.online_medical ? (
                <>
                  <ItemSingle>
                    <div className="title">オンライン診療対象者</div>
                    <div className="total-count">
                      {
                        dashboardInfo?.patients?.online_medical?.condition
                          ?.total
                      }
                      <span className="human">人</span>
                    </div>
                    <div className="seperate-count">
                      <div>
                        男:
                        {
                          dashboardInfo?.patients?.online_medical?.condition
                            ?.gender_0
                        }
                      </div>
                      <div>
                        女:
                        {
                          dashboardInfo?.patients?.online_medical?.condition
                            ?.gender_0
                        }
                      </div>
                      <div>
                        回答なし:
                        {
                          dashboardInfo?.patients?.online_medical?.condition
                            ?.gender_0
                        }
                      </div>
                    </div>
                  </ItemSingle>
                  {dashboardInfo?.patients?.online_medical
                    ?.is_household_based ? (
                    <>
                      <ItemSingle>
                        <div className="title">オンライン診療世帯対象者</div>
                        <div className="total-count">
                          {
                            dashboardInfo?.patients?.online_medical
                              ?.not_condition?.total
                          }
                          <span className="human">人</span>
                        </div>
                        <div className="seperate-count">
                          <div>
                            男:
                            {
                              dashboardInfo?.patients?.online_medical
                                ?.not_condition?.gender_0
                            }
                          </div>
                          <div>
                            女:
                            {
                              dashboardInfo?.patients?.online_medical
                                ?.not_condition?.gender_1
                            }
                          </div>
                          <div>
                            回答なし:
                            {
                              dashboardInfo?.patients?.online_medical
                                ?.not_condition?.gender_2
                            }
                          </div>
                        </div>
                      </ItemSingle>
                    </>
                  ) : (
                    <></>
                  )}
                </>
              ) : (
                <></>
              )}
            </React.Fragment>
          )}
        </ItemsWrapper>
        <SectionLabel>相談数</SectionLabel>
        <ItemsWrapper>
          <ItemSingle>
            <div className="title">{screens.md ? "総件数" : "相談総数"}</div>
            <div className="total-count">
              {dashboardInfo?.consultations?.total}
              <span className="human">件</span>
            </div>
            <div className="seperate-count">
              <div>男:{dashboardInfo?.consultations?.gender_0}</div>
              <div>女:{dashboardInfo?.consultations?.gender_1}</div>
              <div>回答なし:{dashboardInfo?.consultations?.gender_2}</div>
            </div>
          </ItemSingle>
          {dashboardInfo?.consultations?.condition ? (
            <ItemSingle>
              <div className="title">
                {screens.md ? "対象者件数" : "医療相談対象者"}
              </div>
              <div className="total-count">
                {dashboardInfo?.consultations?.condition?.total}
                <span className="human">件</span>
              </div>
              <div className="seperate-count">
                <div>
                  男:{dashboardInfo?.consultations?.condition?.gender_0}
                </div>
                <div>
                  女:{dashboardInfo?.consultations?.condition?.gender_1}
                </div>
                <div>
                  回答なし:{dashboardInfo?.consultations?.condition?.gender_2}
                </div>
              </div>
            </ItemSingle>
          ) : (
            <></>
          )}
          {dashboardInfo?.consultations?.not_condition &&
          dashboardInfo?.patients?.consultation &&
          dashboardInfo?.patients?.consultation?.is_household_based ? (
            <ItemSingle>
              <div className="title">
                {screens.md ? "世帯対象者件数" : "医療相談世帯対象者"}
              </div>
              <div className="total-count">
                {dashboardInfo?.consultations?.not_condition?.total}
                <span className="human">件</span>
              </div>
              <div className="seperate-count">
                <div>
                  男:{dashboardInfo?.consultations?.not_condition?.gender_0}
                </div>
                <div>
                  女:{dashboardInfo?.consultations?.not_condition?.gender_1}
                </div>
                <div>
                  回答なし:
                  {dashboardInfo?.consultations?.not_condition?.gender_2}
                </div>
              </div>
            </ItemSingle>
          ) : (
            <></>
          )}
        </ItemsWrapper>
      </WrapperContent>
    </Box>
  );
};

export default Dashboard;
